import { getAuth } from 'firebase/auth';
import type {
  TagPatchIntent,
  ThreadTagFilter,
  ThreadTagsDoc,
} from '../services/threadTags';

function functionsBaseUrl(): string {
  const override = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  return override
    ? override.replace(/\/$/, '')
    : `https://us-central1-${
        process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3'
      }.cloudfunctions.net`;
}

function endpoint(path: string): string {
  return `${functionsBaseUrl()}/${path}`;
}

async function authToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken();
    return token || null;
  } catch {
    return null;
  }
}

export interface ThreadTagsPatchResponse {
  success: boolean;
  outcome: 'applied' | 'merged' | 'noop' | 'rejected' | 'forbidden';
  doc: ThreadTagsDoc | null;
  violations: string[];
  /** Registry-authoritative participant ids for the thread (owner picker). */
  participantIds?: string[];
  /** False when the caller is a `viewer` in this thread (read-only). */
  canWrite?: boolean;
}

export async function readThreadTags(threadId: string): Promise<ThreadTagsDoc | null> {
  const id = (threadId || '').trim();
  if (!id) return null;
  const token = await authToken();
  if (!token) return null;
  try {
    const res = await fetch(endpoint(`threadTagsRead?threadId=${encodeURIComponent(id)}`), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => null);
    // `ok` = a stored shared doc; `default` = authorized but nothing written
    // yet (version 0). Both are usable state for the UI.
    const decision = body?.decision;
    if ((decision === 'ok' || decision === 'default') && body?.doc) {
      return body.doc as ThreadTagsDoc;
    }
    return null;
  } catch {
    return null;
  }
}

export async function patchThreadTags(params: {
  threadId: string;
  workspaceId: string;
  intent: TagPatchIntent;
  ifMatchVersion?: number;
}): Promise<ThreadTagsPatchResponse> {
  const threadId = (params.threadId || '').trim();
  const workspaceId = (params.workspaceId || '').trim();
  if (!threadId || !workspaceId) {
    return { success: false, outcome: 'rejected', doc: null, violations: ['invalid_thread_or_workspace'] };
  }
  const token = await authToken();
  if (!token) return { success: false, outcome: 'forbidden', doc: null, violations: [] };
  try {
    const res = await fetch(endpoint('threadTagsPatch'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        threadId,
        workspaceId,
        intent: params.intent,
        ifMatchVersion: params.ifMatchVersion,
      }),
    });
    const body = await res.json().catch(() => null);
    const outcome = (body?.outcome || (res.status === 403 ? 'forbidden' : 'rejected')) as ThreadTagsPatchResponse['outcome'];
    return {
      success: body?.success === true,
      outcome,
      doc: body?.doc ? (body.doc as ThreadTagsDoc) : null,
      violations: Array.isArray(body?.violations) ? body.violations.map((v: unknown) => String(v)) : [],
      participantIds: Array.isArray(body?.participantIds)
        ? body.participantIds.map((v: unknown) => String(v))
        : undefined,
      canWrite: typeof body?.canWrite === 'boolean' ? body.canWrite : undefined,
    };
  } catch {
    return { success: false, outcome: 'rejected', doc: null, violations: ['network_error'] };
  }
}

export interface ThreadTagsListResult {
  /**
   * Server-authoritative enablement for this caller. The UI hides tag surfaces
   * when false, which is what makes an ops-doc rollback take effect without a
   * frontend redeploy.
   */
  enabled: boolean;
  docs: ThreadTagsDoc[];
  /** Per-thread write permission + participant ids, parallel to `docs`. */
  threads: Array<{ threadId: string; canWrite: boolean; participantIds: string[] }>;
}

/**
 * List the caller's shared thread tags.
 *
 * Returns `null` when the listing could not be completed (no auth, network
 * error, backend failure). `null` is deliberately distinct from
 * `{ docs: [] }` — "I could not load tags" must not be rendered as "none of
 * your threads have tags", or an active filter would hide every thread.
 */
export async function listThreadTags(params: {
  workspaceId?: string;
  filter?: ThreadTagFilter;
}): Promise<ThreadTagsListResult | null> {
  const token = await authToken();
  if (!token) return null;
  const workspaceId = (params.workspaceId || '').trim();
  try {
    const res = await fetch(endpoint('threadTagsList'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      // `workspaceId` is an optional narrowing hint only; the server authorizes
      // on thread participation, so a participant invited from another account
      // still sees the shared tags.
      body: JSON.stringify({
        ...(workspaceId ? { workspaceId } : {}),
        filter: params.filter ?? {},
      }),
    });
    if (!res.ok) return null;
    const body = await res.json().catch(() => null);
    if (body?.success !== true) return null;
    return {
      enabled: body?.enabled === true,
      docs: (Array.isArray(body?.docs) ? body.docs : []) as ThreadTagsDoc[],
      threads: (Array.isArray(body?.threads) ? body.threads : []).map((row: any) => ({
        threadId: String(row?.threadId ?? ''),
        canWrite: row?.canWrite === true,
        participantIds: Array.isArray(row?.participantIds)
          ? row.participantIds.map((v: unknown) => String(v))
          : [],
      })),
    };
  } catch {
    return null;
  }
}
